import { Prohibit } from "@phosphor-icons/react"
import {
  Checkbox,
  FormQuerySelector,
  RadioGroup,
  Toast,
} from "@vesatogo/grass-core"
import toast from "react-hot-toast"
import { useSearchParams } from "react-router-dom"
import { ExcelFileUpload } from "~/components/ExcelFileUpload"
import { FormCard } from "~/components/FormCard"
import UserDetailsCard from "~/components/UserDetailsCard"
import { TradeKinds, UserKinds } from "~/constants/Kinds"
import { Permission } from "~/constants/Permissions"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import { USER_KINDS } from "~/constants/static.items"
import {
  useAllGroupsQuery,
  useAllUnRegisteredUsersQuery,
  useAuthenticatedUserQuery,
  useUsersQuery,
} from "~/generated/graphql"

type WhoIsTradingProps = {
  showBuyer?: boolean
  showSeller?: boolean
  inputProps?: any
  form?: any
  creditForm?: any
  isDisabled?: boolean
  setPackingOpen?: () => void
  disableBuyer?: boolean
  useCredit?: boolean
  multipleTrader?: boolean
  multipleSeller?: boolean
  uploadSeller?: boolean
  uploadBuyer?: boolean
  requiredBuyer?: boolean
  showAllowMultipleCommodity?: boolean
  creditFetching?: boolean
}

function getOptionLabel({ is_verified, name, is_blocked }) {
  const statusLabel =
    is_verified === false
      ? "Rejected"
      : is_verified === true
      ? "Verified"
      : "Pending Verification"

  return (
    <div className="flex justify-between">
      <div>
        <p>
          {name} | {statusLabel}
        </p>
      </div>
      {is_blocked && <Prohibit size={22} color="red" weight="fill" />}
    </div>
  )
}

export const WhoIsTrading = ({
  form,
  inputProps,
  showBuyer = true,
  isDisabled,
  setPackingOpen,
  disableBuyer,
  creditForm,
  useCredit = true,
  multipleTrader = false,
  multipleSeller = false,
  uploadSeller = false,
  uploadBuyer = false,
  showSeller = true,
  requiredBuyer = true,
  showAllowMultipleCommodity = false,
  creditFetching = false,
}: WhoIsTradingProps) => {
  const [{ data }] = useAuthenticatedUserQuery()
  const me = data?.me

  const [searchParams, setSearchParams] = useSearchParams()
  const newHishobPatti = Boolean(searchParams.get("new"))

  const customStyles = {
    option: (base: any, state: any) => {
      const isBlocked = state.data.is_blocked

      return {
        ...base,
        cursor: isBlocked ? "not-allowed" : "pointer",
        backgroundColor: isBlocked
          ? "#FFDDDD"
          : state.isSelected
          ? "#799700"
          : state?.isFocused
          ? "#DDE2C8"
          : undefined,

        color: isBlocked ? "#F13434" : state.isSelected ? "white" : undefined,
      }
    },
  }

  const { values: state } = form
  const isBookingTrade = state?.kind === TradeKinds.Booking
  const isHishobTrade = state?.kind === TradeKinds.HishobPatti
  const hideCreditDetails = !useCredit
    ? true
    : !isBookingTrade && !isHishobTrade

  const buyerGroups = ["Trader"]
  if (me?.permissions?.includes(Permission.SelectFarmerInBuyer)) {
    buyerGroups.push("Farmer")
  }

  return (
    <FormCard
      title="Who is trading"
      action={
        setPackingOpen || showAllowMultipleCommodity ? (
          <div className="flex flex-wrap items-center gap-4">
            {showAllowMultipleCommodity ? (
              <Checkbox
                label="Allow Multiple Commodity"
                checked={Boolean(state.allow_multiple_commodity)}
                disabled={
                  isDisabled ||
                  state?.parent_trade?.is_multiple_commodity === false
                }
                onChange={() => {
                  const checked = !state.allow_multiple_commodity
                  const hasMultipleCommodityItems =
                    (state.trade_commodity_items?.length ?? 0) > 1 ||
                    state.trades?.some(
                      (t: any) => (t.commodity_items?.length ?? 0) > 1
                    )
                  if (!checked && hasMultipleCommodityItems) {
                    toast.custom(
                      <Toast
                        title="Cannot disable multiple commodity"
                        subtitle="Remove extra commodities before turning this off."
                        intent="danger"
                      />
                    )
                    return
                  }
                  inputProps?.("allow_multiple_commodity")?.onChange(checked)
                }}
              />
            ) : null}
            {setPackingOpen ? (
              <button
                type="button"
                onClick={setPackingOpen}
                className="text-blue-500 underline font-400 italic"
              >
                Packing Material
              </button>
            ) : null}
          </div>
        ) : null
      }
    >
      <div className="flex sm:flex-row flex-col w-full gap-4">
        {showSeller && (
          <div className={"w-full sm:w-1/2"}>
            {multipleSeller && (
              <>
                <RadioGroup
                  className={"mb-3"}
                  label="Supplier Type"
                  required
                  {...inputProps("seller_source_kind")}
                  disabled={
                    (state.id && (state?.seller || state?.seller_external)) ||
                    isDisabled
                  }
                >
                  {USER_KINDS.map((kind, i) => (
                    <Checkbox
                      key={i}
                      type={"radio"}
                      value={kind.id}
                      label={kind.name}
                      disabled={
                        (state.id &&
                          (state?.seller || state?.seller_external)) ||
                        isDisabled
                      }
                    />
                  ))}
                </RadioGroup>
                <FormQuerySelector
                  required={true}
                  isMulti={multipleSeller}
                  maxVisibleItems={5}
                  isDisabled={disableBuyer ?? isDisabled}
                  label="Select Group"
                  className={"mb-2"}
                  serverSideQuery
                  dataHook={useAllGroupsQuery}
                  variables={{ groups: buyerGroups }}
                  {...inputProps("group", {
                    group: SharedEntityGroup.KiteGroup,
                  })}
                  selectorClassName="!border-none"
                  closeMenuOnSelect={!multipleSeller}
                />
              </>
            )}
            {state?.seller_source_kind === UserKinds.ExternalDatabase ? (
              <FormQuerySelector
                dataHook={useAllUnRegisteredUsersQuery}
                {...inputProps("seller_external", {})}
                label="Supplier Name"
                getOptionLabel={item => {
                  return item?.first_name
                }}
                serverSideQuery
                maxVisibleItems={5}
                isMulti={multipleSeller}
                isDisabled={isDisabled}
              />
            ) : (
              <FormQuerySelector
                required={true}
                isMulti={multipleSeller}
                isDisabled={isDisabled}
                label="Supplier Name"
                styles={customStyles}
                className={"mb-2"}
                serverSideQuery
                dataHook={useUsersQuery}
                variables={{
                  groups: multipleSeller
                    ? state?.group?.map(gr => gr?.name) || [""]
                    : ["Farmer", "Trader"],
                }}
                getOptionLabel={getOptionLabel}
                maxVisibleItems={2}
                closeMenuOnSelect={!multipleSeller}
                {...inputProps("seller", {
                  group: SharedEntityGroup.KiteUser,
                })}
                helperText={
                  state.seller?.length > 0
                    ? `${state.seller?.length} Supplier Selected`
                    : ""
                }
              />
            )}
            {uploadSeller && (
              <ExcelFileUpload
                form={form}
                fieldName="seller_file"
                displayName="seller_list"
                isDisabled={isDisabled || !state?.seller_source_kind}
                helperText={
                  !state?.seller_source_kind
                    ? "Select Supplier Type to upload file"
                    : ""
                }
                userSourceKind={
                  state?.seller_source_kind === UserKinds.ExternalDatabase
                    ? "external"
                    : "internal"
                }
              />
            )}
            {!multipleSeller && <UserDetailsCard user={form.values.seller} />}
          </div>
        )}
        {showBuyer && (
          <div className={"w-full sm:w-1/2"}>
            {multipleTrader && (
              <>
                <RadioGroup
                  className={"mb-3"}
                  label="Buyer Type"
                  required
                  {...inputProps("buyer_source_kind")}
                  disabled={
                    (state.id && (state?.buyer || state?.buyer_external)) ||
                    isDisabled
                  }
                >
                  {USER_KINDS.map((kind, i) => (
                    <Checkbox
                      key={i}
                      type={"radio"}
                      value={kind.id}
                      label={kind.name}
                      disabled={
                        (state.id && (state?.buyer || state?.buyer_external)) ||
                        isDisabled
                      }
                    />
                  ))}
                </RadioGroup>
                <FormQuerySelector
                  required={true}
                  isMulti={multipleTrader}
                  maxVisibleItems={5}
                  isDisabled={disableBuyer ?? isDisabled}
                  label="Select Group"
                  className={"mb-2"}
                  serverSideQuery
                  dataHook={useAllGroupsQuery}
                  variables={{ groups: buyerGroups }}
                  closeMenuOnSelect={!multipleTrader}
                  {...inputProps("group", {
                    group: SharedEntityGroup.KiteGroup,
                  })}
                  selectorClassName="!border-none"
                />
              </>
            )}
            {state?.buyer_source_kind === UserKinds.ExternalDatabase ? (
              <FormQuerySelector
                dataHook={useAllUnRegisteredUsersQuery}
                {...inputProps("buyer_external", {})}
                label="Buyer Name"
                getOptionLabel={item => {
                  return item?.first_name
                }}
                maxVisibleItems={5}
                serverSideQuery
                isMulti={multipleTrader}
                isDisabled={isDisabled}
              />
            ) : (
              <FormQuerySelector
                required={requiredBuyer}
                isMulti={multipleTrader}
                isDisabled={disableBuyer}
                label="Buyer Name"
                styles={customStyles}
                className={"mb-2"}
                serverSideQuery
                dataHook={useUsersQuery}
                variables={{
                  groups: multipleTrader
                    ? state?.group?.map(gr => gr?.name) || [""]
                    : buyerGroups,
                }}
                getOptionLabel={getOptionLabel}
                maxVisibleItems={2}
                closeMenuOnSelect={!multipleTrader}
                {...inputProps("buyer", {
                  group: SharedEntityGroup.KiteUser,
                })}
                helperText={
                  state.buyer?.length > 0
                    ? `${state.buyer?.length} Buyers Selected`
                    : ""
                }
                // isDisabled={
                //   (disableBuyer || isDisabled) &&
                //   (state?.parent_trade?.kind !== TradeKinds.SaudaPavti
                //     ? true
                //     : newHishobPatti
                //     ? false
                //     : state?.status !== "Draft")
                // }
              />
            )}
            {uploadBuyer && (
              <ExcelFileUpload
                form={form}
                fieldName="buyer_file"
                displayName="buyer_list"
                isDisabled={isDisabled || !state?.buyer_source_kind}
                helperText={
                  !state?.buyer_source_kind
                    ? "Select Buyer Type to upload file"
                    : ""
                }
                userSourceKind={
                  state?.buyer_source_kind === UserKinds.ExternalDatabase
                    ? "external"
                    : "internal"
                }
              />
            )}
            {!multipleTrader && (
              <UserDetailsCard
                user={form.values.buyer}
                type="buyer"
                creditForm={creditForm}
                hideCreditDetails={hideCreditDetails}
                creditFetching={creditFetching}
              />
            )}
          </div>
        )}
      </div>
    </FormCard>
  )
}
